import { Injectable } from '@angular/core';
import { AppStoreConfig } from '@ddt-web/data-service';
import { State, StoreEntity, StoreService } from '@ddt-web/store-service';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

/**
 * Service to handle store interactions for fund level features e.g. IQ Fund Tags or IQ Dismissal/Acceptance (Interactions) of Statuses
 * The service contains generic logic for updating an entity store where each entity is one fund
 * Called by the FundPreferenceService
 * Each type of fund level feature should extend this method to correctly handle store interactions for that feature
 */
@Injectable({
	providedIn: 'root'
})
export abstract class FundPreferenceStoreService {
	constructor(protected storeConfig: AppStoreConfig, protected storeService: StoreService) {}

	public getAll<T extends StoreEntity<S>, S>(store: string): Observable<T> {
		return this.storeService.selectAll<S>(this.storeConfig[store]).pipe(
			filter((fundState: State<S>) => !fundState.isLoading),
			map((fundState: State<S>) => <T>fundState.entities)
		);
	}

	public removeAllById(fundIds: string[], store: string): void {
		this.storeService.removeMany(this.storeConfig[store], fundIds);
	}

	public setPreferenceData<T>(preferenceData: T[], store: string): void {
		this.storeService.upsertMany(this.storeConfig[store], this.getValidFundPreferences(preferenceData));
	}

	public getPreferencesById<T>(fundId: string, store: string): Observable<StoreEntity<T>> {
		return this.storeService.selectById<T>(this.storeConfig[store], fundId);
	}

	/**
	 * Method to filter out valid fund preferences
	 * Called by setPreferenceData which will ensure only valid preferences are added to the store
	 * Must be implemented in the fund feature library as each fund feature will be unique in its structure
	 * @param preferences the list of fund preferences to store
	 */
	protected abstract getValidFundPreferences<T>(preferences: T[]): T[];
}
